import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';
import useScrollProgress from '../hooks/useScrollProgress';

const Experience = () => {
  const { portfolioData, loading } = usePortfolio();
  const progress = useScrollProgress();
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef([]);
  const work = portfolioData?.work || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Present';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    if (work.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const idx = Number(visible[0].target.dataset.index);
          setActiveIndex(idx);
        }
      },
      { threshold: [0.35, 0.6], rootMargin: '-10% 0px -30% 0px' }
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [work.length]);

  if (loading || !portfolioData) {
    return null;
  }

  return (
    <section id="experience" data-story-section className="py-32 bg-zinc-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,107,53,0.05),transparent_70%)]"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16 space-y-3">
          <h2 className="text-sm font-semibold text-orange-500 uppercase tracking-[0.3em]">Career Journey</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            Proof of work, told as a scroll story.
          </h3>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Center line with animated fill */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 w-px bg-gradient-to-b from-orange-500 to-amber-400 transition-all duration-300"
              style={{ height: `${((activeIndex + 1) / work.length) * 100}%` }}
            />
          </div>

          <div className="space-y-12">
            {work.map((job, index) => {
              const alignLeft = index % 2 === 0;
              return (
                <div
                  key={index}
                  data-index={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`relative flex ${alignLeft ? 'md:justify-start' : 'md:justify-end'} justify-center py-2`}
                  style={{
                    transform: `translateY(calc(var(--page-progress, 0) * ${alignLeft ? '6px' : '-6px'}))`,
                    transition: 'transform 150ms ease-out',
                  }}
                >
                  {/* Dot */}
                  <div className={`absolute left-1/2 -translate-x-1/2 top-8 w-6 h-6 rounded-full border-4 border-zinc-950 transition-all duration-200 ${
                    activeIndex === index
                      ? 'bg-orange-500 shadow-[0_0_24px_rgba(255,107,53,0.7)]'
                      : 'bg-zinc-800 shadow-[0_0_12px_rgba(0,0,0,0.5)]'
                  }`}></div>

                  {/* Card */}
                  <div
                    className={`w-full md:w-[calc(50%-40px)] ${alignLeft ? 'md:pr-12' : 'md:pl-12'} mt-4`}
                  >
                    <div className={`glass p-8 rounded-3xl border transition-all duration-300 group hover:-translate-y-1 ${
                      activeIndex === index ? 'border-orange-500/40 shadow-[0_20px_40px_rgba(255,107,53,0.12)]' : 'border-orange-500/10 hover:border-orange-500/30'
                    }`}>
                      <div className="flex items-start gap-4 mb-6">
                        <div className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          <Briefcase size={28} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-2xl font-bold text-white mb-2 group-hover:text-orange-500 transition-colors">{job.position}</h4>
                          <p className="text-lg font-semibold text-orange-400">{job.company}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                          <MapPin size={18} className="text-orange-500" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={18} className="text-orange-500" />
                          <span>{formatDate(job.startDate)} - {formatDate(job.endDate)}</span>
                        </div>
                      </div>

                      <ul className="space-y-3">
                        {job.highlights.slice(0, 3).map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-300">
                            <span className="text-orange-500 mt-1.5 text-sm">•</span>
                            <span className="text-sm leading-relaxed">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
